import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useAuth } from '../auth/AuthProvider'
import { useTenant } from '../tenant/TenantProvider'
import { conversationAttentionQueryKey } from '../shared/conversation-attention'
import {
  firebaseConversationRepository,
  type ConversationRepository,
} from './conversations-repository'
import styles from './ConversationsPage.module.css'
import { useConversationInbox } from './use-conversation-inbox'
import { useConversationSearch } from './use-conversation-search'
import { useConversationThread } from './use-conversation-thread'
import { mergeConversations, mergeInboxPages } from './conversations-pagination'
import {
  ConversationInbox,
  type ConversationSearchView,
} from './components/ConversationInbox'
import { PatientDetail } from './components/PatientDetail'
import { ThreadPane } from './components/ThreadPane'
import {
  InteractionCallsPane,
  type CallsViewCurrency,
} from './components/InteractionCallsPane'
import type { Conversation, ConversationFilter } from './conversations-model'
import type {
  AttentionFilter,
  ConversationStateFilter,
  IntentFilter,
} from './conversations-model'
import {
  createInteractionAggregateRouteRepository,
  type InteractionAggregateRouteRepository,
  legacyInteractionAggregateId,
} from './interaction-aggregate-repository'
import { useInteractionAggregateRoute } from './use-interaction-aggregate-route'
import { useResolvedInteractionAggregate } from './use-resolved-interaction-aggregate'
import {
  createInteractionCallsRepository,
  type InteractionCallsRepository,
} from './interaction-calls-repository'
import {
  createInteractionOperationalRepository,
  type InteractionOperationalRepository,
} from './interaction-operational-repository'
import { useInteractionOperationalState } from './use-interaction-operational-state'
import { useActionRequests } from './use-action-requests'
import { matchesConversationFilters } from './conversation-filters'
import {
  createConversationOwnershipRepository,
  type ConversationOwnershipRepository,
} from './conversation-ownership-repository'
import { useConversationOwnership } from './use-conversation-ownership'
import { useConversationSmsRoute } from './use-conversation-sms-route'
import {
  createInteractionContextRepository,
  type InteractionContextRepository,
} from './interaction-context-repository'
import { useInteractionContext } from './use-interaction-context'
import {
  createContactDirectoryRepository,
  type ContactDirectoryRepository,
} from './contact-directory-repository'
import { StartConversationDialog } from './components/StartConversationDialog'
import {
  createInternalNotesRepository,
  type InternalNotesRepository,
} from './internal-notes-repository'
import { useConversationFilterRoute } from './use-conversation-filter-route'
import {
  initialWorkspaceTabForConversation,
  type ConversationWorkspaceTab,
} from './conversation-workspace-tab'

type MobileView = 'inbox' | 'thread' | 'details'

// Needs attention and notifications are projected from Action Requests by an
// RTDB trigger; one more refresh after this lag shows its result.
const PROJECTION_SETTLE_MS = 4_000
// A search of the clinic's whole list starts once typing pauses.
const SEARCH_DEBOUNCE_MS = 300

const unavailableCallsRepository: InteractionCallsRepository = {
  getCalls: () => Promise.reject(new Error('Call repository unavailable')),
  getRecording: () => Promise.reject(new Error('Call repository unavailable')),
  getTranscript: () => Promise.reject(new Error('Call repository unavailable')),
  getQualityAnalysis: () =>
    Promise.reject(new Error('Call repository unavailable')),
}

export function ConversationsContent({
  tenantId,
  repository,
  interactionAggregateRouteRepository,
  interactionCallsRepository = unavailableCallsRepository,
  interactionOperationalRepository,
  conversationOwnershipRepository,
  interactionContextRepository,
  contactDirectoryRepository,
  internalNotesRepository,
  initialAggregateId,
  initialContactQuery,
  initialIntentFilter = 'Any open request',
  onIntentFilterRouteChange,
}: {
  tenantId: string
  repository: ConversationRepository
  interactionAggregateRouteRepository?: InteractionAggregateRouteRepository
  interactionCallsRepository?: InteractionCallsRepository
  interactionOperationalRepository?: InteractionOperationalRepository
  conversationOwnershipRepository?: ConversationOwnershipRepository
  interactionContextRepository?: InteractionContextRepository
  contactDirectoryRepository?: ContactDirectoryRepository
  internalNotesRepository?: InternalNotesRepository
  initialAggregateId?: string | null
  initialContactQuery?: string | null
  initialIntentFilter?: IntentFilter
  onIntentFilterRouteChange?: (filter: IntentFilter) => void
}) {
  const inbox = useConversationInbox(repository, tenantId)
  const [query, setQuery] = useState('')
  const trimmedQuery = query.trim()
  const [searchQuery, setSearchQuery] = useState('')
  useEffect(() => {
    if (!trimmedQuery) {
      setSearchQuery('')
      return
    }
    const timeout = window.setTimeout(
      () => setSearchQuery(trimmedQuery),
      SEARCH_DEBOUNCE_MS
    )
    return () => window.clearTimeout(timeout)
  }, [trimmedQuery])
  const search = useConversationSearch(
    repository,
    tenantId,
    inbox.mode,
    searchQuery
  )
  // Matches from the clinic's whole list, with the live newest page on top so
  // a matching conversation's latest activity shows as it arrives.
  const searchResults = useMemo(
    () =>
      search.status === 'ready'
        ? mergeInboxPages(inbox.newestPage, search.pages)
        : [],
    [inbox.newestPage, search.pages, search.status]
  )
  const searchView: ConversationSearchView =
    !trimmedQuery || search.status === 'unsupported'
      ? 'off'
      : searchQuery !== trimmedQuery
        ? 'loading'
        : search.status === 'ready'
          ? 'ready'
          : search.status === 'error' || search.status === 'forbidden'
            ? 'error'
            : 'loading'
  // Every conversation this workspace has read, for selection and status.
  const selectableConversations = useMemo(
    () => mergeConversations(inbox.conversations, searchResults),
    [inbox.conversations, searchResults]
  )
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
    null
  )
  // A conversation opened from search results stays open when the search
  // changes or clears, until it is hidden or another one is selected.
  const [selectedSnapshot, setSelectedSnapshot] = useState<Conversation | null>(
    null
  )
  const liveSelectedConversation =
    selectableConversations.find(
      (conversation) => conversation.conversationId === selectedPatientId
    ) ?? null
  useEffect(() => {
    if (liveSelectedConversation) setSelectedSnapshot(liveSelectedConversation)
  }, [liveSelectedConversation])
  const selectedConversation =
    liveSelectedConversation ??
    (selectedPatientId !== null &&
    selectedSnapshot?.conversationId === selectedPatientId &&
    !inbox.newestPage?.hiddenPatientIds.has(selectedPatientId)
      ? selectedSnapshot
      : null)
  const operationalConversations = useMemo(
    () =>
      selectedConversation && !liveSelectedConversation
        ? [...selectableConversations, selectedConversation]
        : selectableConversations,
    [liveSelectedConversation, selectableConversations, selectedConversation]
  )
  const operational = useInteractionOperationalState(
    interactionOperationalRepository,
    tenantId,
    operationalConversations
  )
  const refreshOperationalState = operational.refresh
  const updateOperationalState = operational.update
  const [mobileView, setMobileView] = useState<MobileView>('inbox')
  const [workspaceTab, setWorkspaceTab] =
    useState<ConversationWorkspaceTab>('sms')
  const [channelFilter, setChannelFilter] = useState<ConversationFilter>('All')
  const [attentionFilter, setAttentionFilter] =
    useState<AttentionFilter>('Everything')
  const [intentFilter, setIntentFilter] =
    useState<IntentFilter>(initialIntentFilter)
  const [stateFilter, setStateFilter] =
    useState<ConversationStateFilter>('Active')
  const [operationalMutation, setOperationalMutation] = useState<
    'read' | 'review' | 'star' | null
  >(null)
  const [startConversationOpen, setStartConversationOpen] = useState(false)
  const [pendingStartedAggregateId, setPendingStartedAggregateId] = useState<
    string | null
  >(null)
  const [callsView, setCallsView] = useState<CallsViewCurrency | null>(null)
  const startConversationTrigger = useRef<HTMLButtonElement | null>(null)
  const readAttempt = useRef<string | null>(null)
  const handledInitialAggregate = useRef<string | null>(null)
  const threadHeading = useRef<HTMLDivElement>(null)
  const detailsTrigger = useRef<HTMLButtonElement>(null)
  const detailPane = useRef<HTMLElement>(null)
  const previousMobileView = useRef<MobileView>('inbox')
  const selectedOperationalState = selectedConversation?.aggregateId
    ? (operational.states[selectedConversation.aggregateId] ?? null)
    : null
  const resolvedAggregate = useResolvedInteractionAggregate(
    interactionAggregateRouteRepository,
    tenantId,
    selectedConversation
  )
  const selectedAggregateId = resolvedAggregate.aggregateId
  const interactionContext = useInteractionContext(
    interactionContextRepository,
    tenantId,
    selectedAggregateId
  )
  const selectedOwnershipAggregateId =
    selectedConversation?.availableChannels.includes('SMS')
      ? selectedAggregateId
      : null
  const ownership = useConversationOwnership(
    conversationOwnershipRepository,
    tenantId,
    selectedOwnershipAggregateId
  )
  const smsRoute = useConversationSmsRoute(
    conversationOwnershipRepository,
    tenantId,
    selectedOwnershipAggregateId
  )
  const selectedActivityAtMs = selectedConversation?.timestamp ?? null
  const queryClient = useQueryClient()
  // Overview's Needs attention, the notification badge and the notifications
  // read the same authority as each conversation's status, so they refresh
  // as soon as a completion, review or Undo settles.
  const invalidateOutstandingWork = () => {
    void queryClient.invalidateQueries({
      queryKey: conversationAttentionQueryKey(tenantId),
    })
    void queryClient.invalidateQueries({
      queryKey: ['dashboard-notifications', tenantId],
    })
  }
  // Completing or reopening is explicit and staff-triggered only; every
  // command is compare-and-set on the revision staff saw.
  const actionRequests = useActionRequests({
    repository: interactionOperationalRepository,
    tenantId,
    aggregateId: selectedAggregateId,
    operationalState: selectedOperationalState,
    statusRefreshes: operational.refreshes,
    activityAtMs: selectedActivityAtMs,
    onReviewed: updateOperationalState,
    onChanged: async (aggregateId) => {
      invalidateOutstandingWork()
      // The summary's unresolved facts derive from the same Action Requests
      // and follow once the projector has run.
      const invalidateSummary = () =>
        void queryClient.invalidateQueries({
          queryKey: ['interaction-context', tenantId, aggregateId],
        })
      invalidateSummary()
      window.setTimeout(invalidateSummary, PROJECTION_SETTLE_MS)
      await refreshOperationalState()
    },
  })
  const aggregateRoute = useInteractionAggregateRoute(
    interactionAggregateRouteRepository,
    tenantId,
    selectedConversation
  )
  const thread = useConversationThread(
    repository,
    tenantId,
    aggregateRoute.status === 'ready' ? aggregateRoute.conversation : null
  )
  const threadResolution =
    aggregateRoute.status === 'loading'
      ? ({ status: 'loading' } as const)
      : aggregateRoute.status === 'error'
        ? ({ status: 'error', kind: 'transport' } as const)
        : thread.resolution
  // A ready search lists its own results; until then (and if it fails) the
  // matches among conversations already read show, marked as partial.
  const listedConversations =
    searchView === 'off'
      ? inbox.conversations
      : searchView === 'ready'
        ? searchResults
        : selectableConversations
  const visibleConversations = useMemo(() => {
    return listedConversations.filter((conversation) =>
      matchesConversationFilters(
        conversation,
        conversation.aggregateId
          ? operational.states[conversation.aggregateId]
          : undefined,
        {
          query,
          channel: channelFilter,
          attention: attentionFilter,
          intent: intentFilter,
          state: stateFilter,
        }
      )
    )
  }, [
    attentionFilter,
    channelFilter,
    intentFilter,
    listedConversations,
    operational.states,
    query,
    stateFilter,
  ])
  const searchMatchCount = useMemo(
    () =>
      searchView === 'off'
        ? 0
        : listedConversations.filter((conversation) =>
            matchesConversationFilters(conversation, undefined, {
              query,
              channel: 'All',
              attention: 'Everything',
              intent: 'Any open request',
              state: 'Active',
            })
          ).length,
    [listedConversations, query, searchView]
  )

  useEffect(() => {
    setSelectedPatientId(null)
    setSelectedSnapshot(null)
    setMobileView('inbox')
    setQuery('')
    setChannelFilter('All')
    setAttentionFilter('Everything')
    setStateFilter('Active')
    setWorkspaceTab('sms')
    setStartConversationOpen(false)
    setPendingStartedAggregateId(null)
  }, [tenantId])

  useEffect(() => {
    setIntentFilter(initialIntentFilter)
  }, [initialIntentFilter, tenantId])

  useEffect(() => {
    if (!initialAggregateId) {
      handledInitialAggregate.current = null
      return
    }
    if (inbox.status !== 'ready') return
    const selectionKey = `${tenantId}:${initialAggregateId}`
    if (handledInitialAggregate.current === selectionKey) return
    let cancelled = false
    const select = (conversation: Conversation | undefined) => {
      if (cancelled || !conversation) return
      handledInitialAggregate.current = selectionKey
      setSelectedPatientId(conversation.conversationId)
      setWorkspaceTab(initialWorkspaceTabForConversation(conversation))
      setMobileView('thread')
    }
    const canonical = inbox.conversations.find(
      (candidate) => candidate.aggregateId === initialAggregateId
    )
    if (canonical) select(canonical)
    else {
      // Legacy inbox rows omit aggregateId. Reuse the canonical tenant/key
      // identity function; never match display names, phone numbers or time.
      void Promise.all(
        inbox.conversations.map(async (candidate) => {
          if (
            candidate.identityBasis !== 'LEGACY_PATIENT_KEY' ||
            !candidate.patientId
          )
            return undefined
          const aggregateId = await legacyInteractionAggregateId(
            tenantId,
            candidate.patientId
          )
          return aggregateId === initialAggregateId ? candidate : undefined
        })
      )
        .then((matches) => select(matches.find(Boolean)))
        .catch(() => {
          // Failed identity resolution must not select a different conversation.
        })
    }
    return () => {
      cancelled = true
    }
  }, [inbox.conversations, inbox.status, initialAggregateId, tenantId])

  useEffect(() => {
    if (!initialContactQuery || !contactDirectoryRepository) return
    setStartConversationOpen(true)
  }, [contactDirectoryRepository, initialContactQuery])

  useEffect(() => {
    if (!pendingStartedAggregateId) return
    const conversation = inbox.conversations.find(
      (candidate) => candidate.aggregateId === pendingStartedAggregateId
    )
    if (!conversation) return
    setSelectedPatientId(conversation.conversationId)
    setWorkspaceTab(initialWorkspaceTabForConversation(conversation))
    setMobileView('thread')
    setStartConversationOpen(false)
    setPendingStartedAggregateId(null)
  }, [inbox.conversations, pendingStartedAggregateId])

  useEffect(() => {
    if (
      !interactionOperationalRepository ||
      !selectedConversation?.aggregateId ||
      !selectedOperationalState?.unreadForCaller
    ) {
      return
    }
    const threadWorkspaceLoaded =
      aggregateRoute.status === 'ready' &&
      (thread.resolution.status === 'choice-required' ||
        thread.resolution.status === 'empty' ||
        (thread.resolution.status === 'resolved' &&
          thread.messageStatus === 'ready'))
    // A caller with calls but no Flio contact record has no legacy thread
    // route; the visible Calls view then vouches for Voice activity, but only
    // while its latest fetch succeeded and was issued after that activity.
    const callsWorkspaceCurrent =
      workspaceTab === 'calls' &&
      selectedConversation.channel === 'Voice' &&
      callsView?.owner === `${tenantId}:${selectedConversation.aggregateId}` &&
      callsView.currentThroughActivityAtMs >= selectedConversation.timestamp
    if (!threadWorkspaceLoaded && !callsWorkspaceCurrent) return
    const attempt = `${selectedConversation.aggregateId}:${selectedConversation.timestamp}:${selectedOperationalState.readRevision}`
    if (readAttempt.current === attempt) return
    readAttempt.current = attempt
    setOperationalMutation('read')
    void interactionOperationalRepository
      .markRead(
        tenantId,
        selectedConversation.aggregateId,
        selectedOperationalState.readRevision,
        selectedConversation.timestamp
      )
      .then(updateOperationalState)
      .catch(() => {
        readAttempt.current = null
        void refreshOperationalState()
      })
      .finally(() => setOperationalMutation(null))
  }, [
    aggregateRoute.status,
    interactionOperationalRepository,
    callsView,
    refreshOperationalState,
    selectedConversation,
    selectedOperationalState,
    tenantId,
    thread.messageStatus,
    thread.resolution.status,
    updateOperationalState,
    workspaceTab,
  ])

  useEffect(() => {
    if (
      selectedPatientId &&
      !selectedConversation &&
      inbox.status === 'ready'
    ) {
      setSelectedPatientId(null)
      setMobileView('inbox')
    }
  }, [inbox.status, selectedConversation, selectedPatientId])

  useEffect(() => {
    const previous = previousMobileView.current
    previousMobileView.current = mobileView
    const focusFrame = window.requestAnimationFrame(() => {
      if (mobileView === 'thread') {
        if (previous === 'details') detailsTrigger.current?.focus()
        else threadHeading.current?.focus()
      }
      if (mobileView === 'details') {
        const focusable = detailPane.current?.querySelector<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
        ;(focusable ?? detailPane.current)?.focus()
      }
    })
    return () => window.cancelAnimationFrame(focusFrame)
  }, [mobileView])

  useEffect(() => {
    if (mobileView !== 'details') return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setMobileView('thread')
        return
      }
      if (event.key !== 'Tab' || !detailPane.current) return
      const focusable = Array.from(
        detailPane.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      )
      if (focusable.length === 0) {
        event.preventDefault()
        detailPane.current.focus()
        return
      }
      const first = focusable[0]
      const last = focusable.at(-1)
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [mobileView])

  return (
    <div className={styles.workspaceFrame}>
      <div
        {...((startConversationOpen ? { inert: '' } : {}) as {
          inert?: string
        })}
        aria-hidden={startConversationOpen || undefined}
        className={`${styles.workspace} ${styles[`view-${mobileView}`]}`}
      >
        <ConversationInbox
          status={inbox.status}
          conversations={visibleConversations}
          loadedCount={inbox.conversations.length}
          query={query}
          channelFilter={channelFilter}
          attentionFilter={attentionFilter}
          intentFilter={intentFilter}
          stateFilter={stateFilter}
          operationalStates={operational.states}
          operationalStatus={operational.status}
          selectedPatientId={selectedPatientId}
          hasOlder={
            searchView === 'off'
              ? inbox.hasOlder
              : searchView === 'ready' && search.hasOlder
          }
          loadingOlder={
            searchView === 'off' ? inbox.loadingOlder : search.loadingOlder
          }
          search={{
            view: searchView,
            matchCount: searchMatchCount,
            olderFailed: search.olderFailed,
            onRetry: search.retry,
          }}
          onSelect={(conversation) => {
            if (conversation.conversationId !== selectedPatientId) {
              setWorkspaceTab(initialWorkspaceTabForConversation(conversation))
            }
            setSelectedPatientId(conversation.conversationId)
            setSelectedSnapshot(conversation)
            setMobileView('thread')
          }}
          onLoadOlder={() =>
            void (searchView === 'off' ? inbox.loadOlder() : search.loadOlder())
          }
          onQueryChange={setQuery}
          onChannelFilterChange={setChannelFilter}
          onAttentionFilterChange={setAttentionFilter}
          onIntentFilterChange={(filter) => {
            setIntentFilter(filter)
            onIntentFilterRouteChange?.(filter)
          }}
          onStateFilterChange={setStateFilter}
          backgroundInert={mobileView === 'details'}
        />
        <div
          {...((mobileView === 'details' ? { inert: '' } : {}) as {
            inert?: string
          })}
          ref={threadHeading}
          tabIndex={-1}
          className={styles.focusTarget}
          aria-hidden={mobileView === 'details' || undefined}
        >
          <ThreadPane
            conversation={selectedConversation}
            resolution={threadResolution}
            threadSelected={thread.selectedThread !== null}
            messageStatus={thread.messageStatus}
            messages={thread.messages}
            messageHistoryKey={thread.messageOwner}
            hasOlderMessages={thread.hasOlderMessages}
            loadingOlderMessages={thread.loadingOlderMessages}
            onBack={() => setMobileView('inbox')}
            onShowDetails={() => setMobileView('details')}
            detailsTriggerRef={detailsTrigger}
            onChooseThread={thread.chooseThread}
            onChangeThread={thread.clearThreadChoice}
            onLoadOlderMessages={() => void thread.loadOlderMessages()}
            activeTab={workspaceTab}
            onTabChange={setWorkspaceTab}
            callsContent={
              <InteractionCallsPane
                tenantId={tenantId}
                aggregateId={selectedAggregateId}
                repository={interactionCallsRepository}
                active={workspaceTab === 'calls'}
                identityLoading={resolvedAggregate.status === 'loading'}
                activityAtMs={selectedConversation?.timestamp ?? null}
                onCurrentChange={setCallsView}
              />
            }
            operationalState={selectedOperationalState}
            operationalStatus={operational.status}
            operationalBusy={operationalMutation !== null}
            actionRequests={actionRequests}
            onToggleReviewed={
              interactionOperationalRepository &&
              selectedAggregateId &&
              selectedActivityAtMs &&
              selectedOperationalState
                ? () => {
                    setOperationalMutation('review')
                    void interactionOperationalRepository
                      .markReviewed(
                        tenantId,
                        selectedAggregateId,
                        selectedOperationalState.reviewRevision,
                        selectedActivityAtMs,
                        selectedOperationalState.reviewStatus !== 'REVIEWED'
                      )
                      .then(updateOperationalState)
                      .catch(() => void refreshOperationalState())
                      .finally(() => {
                        setOperationalMutation(null)
                        invalidateOutstandingWork()
                      })
                  }
                : undefined
            }
            onToggleStar={
              interactionOperationalRepository &&
              selectedAggregateId &&
              selectedOperationalState
                ? () => {
                    setOperationalMutation('star')
                    void interactionOperationalRepository
                      .setStarred(
                        tenantId,
                        selectedAggregateId,
                        selectedOperationalState.starRevision,
                        !selectedOperationalState.starred
                      )
                      .then(updateOperationalState)
                      .catch(() => void refreshOperationalState())
                      .finally(() => setOperationalMutation(null))
                  }
                : undefined
            }
            ownershipState={ownership.value}
            ownershipStatus={ownership.status}
            ownershipRepository={conversationOwnershipRepository}
            smsRoute={smsRoute.value}
            smsRouteStatus={smsRoute.status}
            tenantId={tenantId}
            aggregateId={selectedOwnershipAggregateId}
            onOwnershipState={ownership.update}
            onRefreshOwnership={ownership.refresh}
            onRefreshSmsRoute={smsRoute.refresh}
          />
        </div>
        {selectedConversation ? (
          <button
            className={styles.detailScrim}
            type="button"
            aria-label="Close patient details"
            onClick={() => setMobileView('thread')}
          />
        ) : null}
        {selectedConversation ? (
          <aside
            ref={detailPane}
            tabIndex={-1}
            className={styles.detailPane}
            aria-label="Patient details"
            role={mobileView === 'details' ? 'dialog' : undefined}
            aria-modal={mobileView === 'details' ? true : undefined}
          >
            {Object.values(interactionContext).some(
              (state) => state.refreshFailed
            ) ? (
              <p role="status">
                Some details could not be refreshed. Showing the last loaded
                details.
              </p>
            ) : null}
            <PatientDetail
              details={selectedConversation.details}
              actionRequests={actionRequests}
              patientContext={interactionContext.patient}
              appointmentContext={interactionContext.appointment}
              insuranceContext={interactionContext.insurance}
              summaryContext={interactionContext.summary}
              onClose={() => setMobileView('thread')}
              tenantId={tenantId}
              internalNotesRepository={internalNotesRepository}
              aggregateId={selectedAggregateId}
            />
          </aside>
        ) : (
          <aside className={styles.detailPane} aria-label="Patient details">
            <div className={styles.detailPlaceholder}>Patient details</div>
          </aside>
        )}
      </div>
      {startConversationOpen && contactDirectoryRepository ? (
        <StartConversationDialog
          tenantId={tenantId}
          repository={contactDirectoryRepository}
          initialQuery={initialContactQuery ?? ''}
          onClose={() => {
            setStartConversationOpen(false)
            window.requestAnimationFrame(() =>
              startConversationTrigger.current?.focus()
            )
          }}
          onStarted={(aggregateId) => {
            setPendingStartedAggregateId(aggregateId)
          }}
        />
      ) : null}
    </div>
  )
}

export function ConversationsPage({
  repository = firebaseConversationRepository,
  interactionAggregateRouteRepository,
  interactionCallsRepository,
  interactionOperationalRepository,
  conversationOwnershipRepository,
  interactionContextRepository,
  contactDirectoryRepository,
  internalNotesRepository,
}: {
  repository?: ConversationRepository
  interactionAggregateRouteRepository?: InteractionAggregateRouteRepository
  interactionCallsRepository?: InteractionCallsRepository
  interactionOperationalRepository?: InteractionOperationalRepository
  conversationOwnershipRepository?: ConversationOwnershipRepository
  interactionContextRepository?: InteractionContextRepository
  contactDirectoryRepository?: ContactDirectoryRepository
  internalNotesRepository?: InternalNotesRepository
}) {
  const auth = useAuth()
  const tenant = useTenant()
  const {
    searchParams,
    intentFilter: routeIntentFilter,
    setIntentFilter: setRouteIntentFilter,
  } = useConversationFilterRoute()
  const currentUser = auth.status === 'authenticated' ? auth.user : null
  const activeAggregateRouteRepository = useMemo(
    () =>
      interactionAggregateRouteRepository ??
      createInteractionAggregateRouteRepository({ getUser: () => currentUser }),
    [interactionAggregateRouteRepository, currentUser]
  )
  const activeInteractionCallsRepository = useMemo(
    () =>
      interactionCallsRepository ??
      createInteractionCallsRepository({ getUser: () => currentUser }),
    [interactionCallsRepository, currentUser]
  )
  const activeInteractionOperationalRepository = useMemo(
    () =>
      interactionOperationalRepository ??
      createInteractionOperationalRepository({ getUser: () => currentUser }),
    [interactionOperationalRepository, currentUser]
  )
  const activeConversationOwnershipRepository = useMemo(
    () =>
      conversationOwnershipRepository ??
      createConversationOwnershipRepository({ getUser: () => currentUser }),
    [conversationOwnershipRepository, currentUser]
  )
  const activeInteractionContextRepository = useMemo(
    () =>
      interactionContextRepository ??
      createInteractionContextRepository({ getUser: () => currentUser }),
    [interactionContextRepository, currentUser]
  )
  const activeContactDirectoryRepository = useMemo(
    () =>
      contactDirectoryRepository ??
      createContactDirectoryRepository({ getUser: () => currentUser }),
    [contactDirectoryRepository, currentUser]
  )
  const activeInternalNotesRepository = useMemo(
    () =>
      internalNotesRepository ??
      createInternalNotesRepository({ getUser: () => currentUser }),
    [internalNotesRepository, currentUser]
  )
  if (!tenant.selectedClinic) return null
  return (
    <ConversationsContent
      tenantId={tenant.selectedClinic.id}
      repository={repository}
      interactionAggregateRouteRepository={activeAggregateRouteRepository}
      interactionCallsRepository={activeInteractionCallsRepository}
      interactionOperationalRepository={activeInteractionOperationalRepository}
      conversationOwnershipRepository={activeConversationOwnershipRepository}
      interactionContextRepository={activeInteractionContextRepository}
      contactDirectoryRepository={activeContactDirectoryRepository}
      internalNotesRepository={activeInternalNotesRepository}
      initialAggregateId={searchParams.get('aggregate')}
      initialContactQuery={searchParams.get('contact')}
      initialIntentFilter={routeIntentFilter}
      onIntentFilterRouteChange={setRouteIntentFilter}
    />
  )
}
